import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusCircle, Send, Check, Loader2 } from "lucide-react";
import { submitVehicleRequest } from "../lib/api";

export const VehicleRequest = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ make: "", model: "", year: "", email: "", notes: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSend = form.make.trim() && form.model.trim() && form.year.trim() && status !== "sending";

  const send = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setStatus("sending");
    try {
      await submitVehicleRequest({
        make: form.make.trim(),
        model: form.model.trim(),
        year: form.year.trim(),
        email: form.email.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      setStatus("sent");
      setForm({ make: "", model: "", year: "", email: "", notes: "" });
    } catch {
      setStatus("error");
    }
  };

  if (!open) {
    return (
      <button data-testid="vehicle-request-open" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-xl bg-elevated border border-steel text-slate-300 px-4 py-2.5 font-mono text-xs uppercase tracking-widest hover:border-cyan/50 hover:text-cyan transition-colors">
        <PlusCircle size={15} /> {t("garage.reqOpen")}
      </button>
    );
  }

  return (
    <form onSubmit={send} data-testid="vehicle-request-form"
      className="w-full rounded-2xl bg-surface border border-cyan/30 p-5 animate-rise">
      <div className="flex items-center gap-2 mb-1">
        <PlusCircle size={16} className="text-cyan" />
        <h2 className="font-heading font-bold uppercase tracking-widest text-sm">{t("garage.reqTitle")}</h2>
      </div>
      <p className="text-slate-400 text-xs mb-4">{t("garage.reqSubtitle")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label={t("garage.reqMake")} value={form.make} onChange={update("make")} testId="vehicle-request-make" required />
        <Field label={t("garage.reqModel")} value={form.model} onChange={update("model")} testId="vehicle-request-model" required />
        <Field label={t("garage.reqYear")} value={form.year} onChange={update("year")} testId="vehicle-request-year" required inputMode="numeric" />
      </div>
      <div className="mt-3">
        <Field label={t("garage.reqEmail")} value={form.email} onChange={update("email")} testId="vehicle-request-email" type="email" />
      </div>
      <div className="mt-3">
        <label className="block font-mono text-[9px] text-slate-500 tracking-widest uppercase mb-1">{t("garage.reqNotes")}</label>
        <textarea data-testid="vehicle-request-notes" value={form.notes} onChange={update("notes")} rows={2}
          className="w-full rounded-lg bg-ink border border-steel/60 px-3 py-2 text-sm text-white focus:border-cyan/60 focus:outline-none resize-none" />
      </div>

      {status === "error" && <p data-testid="vehicle-request-error" className="mt-3 text-xs text-fail">{t("garage.reqError")}</p>}

      <div className="mt-4 flex items-center gap-3">
        {status === "sent" ? (
          <span data-testid="vehicle-request-success" className="inline-flex items-center gap-2 text-pass font-mono text-xs uppercase tracking-widest">
            <Check size={15} /> {t("garage.reqSuccess")}
          </span>
        ) : (
          <button type="submit" data-testid="vehicle-request-send" disabled={!canSend}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan text-ink px-4 py-2.5 font-heading font-bold text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-cyan-hover transition-colors">
            {status === "sending" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {status === "sending" ? t("garage.reqSending") : t("garage.reqSend")}
          </button>
        )}
      </div>
    </form>
  );
};

const Field = ({ label, testId, required, type = "text", inputMode, value, onChange }) => (
  <div>
    <label className="block font-mono text-[9px] text-slate-500 tracking-widest uppercase mb-1">{label}</label>
    <input data-testid={testId} type={type} inputMode={inputMode} value={value} onChange={onChange} required={required}
      className="w-full rounded-lg bg-ink border border-steel/60 px-3 py-2 text-sm text-white focus:border-cyan/60 focus:outline-none" />
  </div>
);
